import React, { useCallback, useState } from "react";
import "./styles.scss";
import { Col, Container, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { MdAdd } from "react-icons/md";
import randomString from "randomstring";
import PageHeader from "../../../../dashboard/components/page-header/PageHeader";
import { useStorage } from "reactfire";
import UserNotification from "../../../../../shared/components/Notification/UserNotification";
import FormValidator from "../../../../../shared/utils/FormValidator";
import firebase from "firebase/app";
import { BarLoader } from "react-spinners";
import { useHistory } from "react-router-dom";
import { zeroPad } from "../../../../../shared/utils/Commons";
import { IoMdDownload } from "react-icons/io";
import { useDropzone } from "react-dropzone";
import { Country, FileContent } from "../../../../../shared/constants/Constant";
import FileTag from "./components/file-tag/FileTag";
import Select from "react-select";
import { useAnnouncements } from "../../../../../contexts/announcement-context/AnnouncementContext";
import TextEditor from "../../../../../shared/components/text-editor/TextEditor";
import { useAppContext } from "../../../../../contexts/app-context/AppContext";

export interface AddAnnouncementDTO {
  id: string;
  title: string;
  description: string;
  date: string;
  duration: number;
  documents: string[];
  postTo: Country[];
}

export interface IState {
  title: string;
  titleError: string;
  description: string;
  descriptionError: string;
  date: Date | null;
  dateError: string;
  time: Time;
  timeError: string;
  duration: number;
  durationError: string;
  fileURLs: string[];
  countries: Country[];
  countriesError: string;
  loading: boolean;
}

interface Time {
  h: number;
  m: number;
}

const initialState: IState = {
  title: "",
  titleError: "",
  description: "",
  descriptionError: "",
  date: null,
  dateError: "",
  time: { h: 0, m: 0 },
  timeError: "",
  duration: 0,
  durationError: "",
  fileURLs: [],
  countries: [],
  countriesError: "",
  loading: false,
};

const customStylesValid = {
  control: (provided: any, state: any) => ({
    // none of react-select's styles are passed to <Control />
    ...provided,
    minHeight: "calc(1.5em + 0.75rem + 10px)",
    border: "solid 2px #006699",
    boxShadow: state.isFocused ? 0 : 0,
    borderColor: state.isFocused ? "#006699" : "#006699",
    "&:hover": {
      borderColor: state.isFocused ? "#006699" : "#006699",
    },
  }),
};

const customStylesInValid = {
  control: (provided: any, state: any) => ({
    // none of react-select's styles are passed to <Control />
    ...provided,
    minHeight: "calc(1.5em + 0.75rem + 10px)",
    border: "solid 2px #e74c3c",
    boxShadow: state.isFocused ? 0 : 0,
    borderColor: state.isFocused ? "#e74c3c" : "#e74c3c",
    "&:hover": {
      borderColor: state.isFocused ? "#e74c3c" : "#e74c3c",
    },
  }),
};

const CreateAnnouncement = () => {
  const [uploadElement, setUploadElement] = useState(null as any);
  const [selectedFiles, setSelectedFiles] = useState<
    { id: string; file: File; url: string }[]
  >([]);
  const [state, setState] = useState(initialState);

  const app = useAppContext();
  const announcements = useAnnouncements();

  const validateData = (): boolean => {
    //title
    const titleValidation = FormValidator.validateString(
      state.title,
      "Title",
      3,
      100
    );
    //date
    const dateValidation =
      state.date === null
        ? { message: "Please set a valid Date.", isValid: false }
        : FormValidator.validateWorkshopStartDate(state.date, "Date");
    //time
    const today = new Date();
    const timeValidation =
      state.date &&
      state.time &&
      dateValidation.isValid &&
      FormValidator.areDatesEqual(new Date(), state.date)
        ? today.getHours() < state.time.h
          ? { message: "", isValid: true }
          : today.getHours() === state.time.h &&
            today.getMinutes() < state.time.m
          ? { message: "", isValid: true }
          : { message: "Please set a valid Start Time.", isValid: false }
        : { message: "", isValid: true };
    //duration
    const durationValidation =
      (state.duration && state.duration > 0) || !dateValidation.isValid
        ? { message: "", isValid: true }
        : { message: "invalid duration", isValid: false };
    //description
    const descriptionValidation = FormValidator.validateQuestionBody(
      state.description,
      "Description",
      3,
      2100
    );
    //postTo
    const postToValidation =
      state.countries.length !== 0
        ? { message: "", isValid: true }
        : { message: "Please add at least one country", isValid: false };

    setState({
      ...state,
      titleError: titleValidation.message,
      descriptionError: descriptionValidation.message,
      dateError: dateValidation.message,
      durationError: durationValidation.message,
      countriesError: postToValidation.message,
      timeError: timeValidation.message,
    });
    return (
      titleValidation.isValid &&
      descriptionValidation.isValid &&
      dateValidation.isValid &&
      durationValidation.isValid &&
      postToValidation.isValid &&
      timeValidation.isValid
    );
  };

  const storage = useStorage().ref();
  const [loading, setLoading] = useState(false);
  const { push } = useHistory();

  const saveData = () => {
    if (validateData()) {
      setLoading(true);
      if (selectedFiles.length > 0) {
        const downloadUrlTasks: { file: File; task: Promise<string> }[] = [];
        const tasks = selectedFiles.map((f) => {
          const task = storage
            .child(`sessionresources/${f.file.name}`)
            .put(f.file);
          task.on(
            firebase.storage.TaskEvent.STATE_CHANGED,
            (snapshot) => {
              const progress =
                ((snapshot.bytesTransferred / snapshot.totalBytes) * 100) /
                selectedFiles.length;
              if (snapshot.state === firebase.storage.TaskState.RUNNING) {
                console.log(`Progress: ${progress}% - ${f.file.name}`);
              }
            },
            (error) => console.log(error.message),
            async () => {
              downloadUrlTasks.push({
                file: f.file,
                task: task.snapshot.ref.getDownloadURL(),
              });
            }
          );

          return task;
        });

        const urls: FileContent[] = [];
        Promise.all(tasks)
          .then(async () => {
            for (let utask of downloadUrlTasks) {
              const downloadURL = await utask.task;
              console.log(downloadURL);
              urls.push({
                url: downloadURL,
                fileName: utask.file?.name,
                type: utask.file.name.substr(
                  utask.file.name.lastIndexOf(".") + 1
                ),
              });
            }

            const dateAt = state.date;
            const fullDate = new Date(
              dateAt?.getFullYear() || 0,
              dateAt?.getMonth() || 0,
              dateAt?.getDate() || -1,
              state.time.h,
              state.time.m
            );

            const data = {
              title: state.title,
              description: state.description,
              date: fullDate?.toString(),
              duration: state.duration,
              fileURLs: urls,
              countries: state.countries,
            };
            announcements.addAnnouncement(data, (status, id) => {
              if (status) {
                UserNotification({
                  isSuccess: true,
                  message: "Notice Successfully Saved!",
                });
                app.pushBackURL("/dashboard/announcements/newannouncement");
                push(`/dashboard/announcements/announcement/${id}`);
                setLoading(false);
              } else {
                UserNotification({
                  isSuccess: false,
                  message: "Notice Failed to Create!",
                });
                setLoading(false);
              }
            });
          })
          .catch((err) => {
            setLoading(false);
            UserNotification({
              isSuccess: false,
              message: "Notice Failed to Create!",
            });
          });
      } else {
        const dateAt = state.date;
        const fullDate = new Date(
          dateAt?.getFullYear() || 0,
          dateAt?.getMonth() || 0,
          dateAt?.getDate() || -1,
          state.time.h,
          state.time.m
        );

        const data = {
          title: state.title,
          description: state.description,
          date: fullDate?.toString(),
          duration: state.duration,
          countries: state.countries,
        };
        announcements.addAnnouncement(data, (status, id) => {
          if (status) {
            UserNotification({
              isSuccess: true,
              message: "Notice Successfully Saved!",
            });
            app.pushBackURL("/dashboard/announcements/newannouncement");
            push(`/dashboard/announcements/announcement/${id}`);
            setLoading(false);
          } else {
            UserNotification({
              isSuccess: false,
              message: "Notice Failed to Create!",
            });
            setLoading(false);
          }
        });
      }
    }
  };

  // const [allTimes, setAllTimes] = useState<string[]>([]);
  const onDrop = useCallback((files) => {
    const file: any = files[0];
    const fileType = file?.name
      .split(".")
      .pop()
      .toLowerCase();
    const exList = "xls,pptx,ppt,png,pdf,jpg,jpeg,gif,docx,doc,xlsx,txt,md".split(
      ","
    );

    let b1 = file && exList.filter((ext) => ext === fileType).length > 0;
    let b2 = file && file?.size <= 105255200;
    let errorMessage = "No file was selected";
    if (b1) errorMessage = "Invalid file format";
    if (b2) errorMessage = " Invalid file size";
    if (b1 && b2) {
      if (file) {
        setSelectedFiles((ps) => {
          ps.push({
            file: file,
            id: randomString.generate(),
            url: "",
          });
          return Object.assign([], ps);
        });
      }
    } else {
      UserNotification({
        isSuccess: false,
        message: errorMessage,
      });
    }
  }, []);
  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  // useEffect(() => {
  //   let times: string[] = [];
  //   for (let i: number = 0; i < 24; i++) {
  //     times.push(`${zeroPad(i, 2)}:00`);
  //     times.push(`${zeroPad(i, 2)}:15`);
  //     times.push(`${zeroPad(i, 2)}:30`);
  //     times.push(`${zeroPad(i, 2)}:45`);
  //   }
  //   setAllTimes(times);
  // }, []);

  return (
    <div className="create-sr-wrapper">
      <PageHeader title="Create Notice" />
      <Container
        fluid
        style={{
          paddingTop: "200px",
          paddingLeft: "50px",
          paddingRight: "50px",
          paddingBottom: "50px",
        }}
      >
        <Row>
          <Col xs={12} className="mb-3">
            <BarLoader
              height={4}
              width={"100%"}
              color={"#006699"}
              loading={loading}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} className="text-right mb-3">
            <div
              className={"save-button"}
              style={
                !loading
                  ? { marginLeft: "15px" }
                  : { marginLeft: "15px", backgroundColor: "#737373" }
              }
              onClick={() => {
                !loading && saveData();
              }}
            >
              Save
            </div>
          </Col>
        </Row>
        <Row className="py-2">
          <Col xs={12}>
            <label className="font-weight-bold form-label">
              Notice Title
              <span className="ml-1" style={{ color: "red" }}>
                *
              </span>
            </label>
            <input
              className={
                state.titleError.length < 1 ? "app-input" : "app-input invalid"
              }
              value={state.title}
              onChange={(e) =>
                setState({ ...state, title: e.target.value, titleError: "" })
              }
            />
            <label className="error-message">{state.titleError}</label>
          </Col>
        </Row>
        <Row className="py-2">
          <Col xs={12}>
            <TextEditor
              initialValue={state.description}
              onEditorChange={(content: any, editor: any) => {
                setState((ps) => ({ ...ps, description: content }));
              }}
            />
            <label className="error-message">{state.descriptionError}</label>
          </Col>
        </Row>
        <Row className="py-2">
          <Col xs={3}>
            <label className="font-weight-bold form-label">
              Date
              <span className="ml-1" style={{ color: "red" }}>
                *
              </span>
            </label>
            <div>
              <DatePicker
                className={
                  state.dateError.length < 1
                    ? "app-input calender-img"
                    : "app-input calender-img invalid"
                }
                onChange={(date: Date) => {
                  setState({ ...state, date: date, dateError: "" });
                }}
                selected={state.date}
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                dateFormat="dd/MM/yyyy"
                placeholderText={"DD/MM/YYYY"}
                popperClassName={"customDatePicker"}
                customInput={<input className="app-input-profile" />}
              />
            </div>
            <label className="error-message">{state.dateError}</label>
          </Col>
          <Col xs={2}>
            <label className="font-weight-bold form-label">
              Time
              <span className="ml-1" style={{ color: "red" }}>
                *
              </span>
            </label>
            <div className="d-flex">
              {/* <select
                className={
                  state.timeError.length < 1
                    ? "app-input mr-1"
                    : "app-input mr-1 invalid"
                }
                value={
                  zeroPad(state.time.h, 2) + ":" + zeroPad(state.time.m, 2)
                }
                onChange={(e) => {
                  const time = e.target.value.split(":");
                  setState({
                    ...state,
                    time: { ...state.time, h: +time[0], m: +time[1] },
                    timeError: "",
                  });
                }}
              >
                {allTimes.map((t, index) => (
                  <option key={index} className="time-selector-item" value={t}>
                    {t}
                  </option>
                ))}
              </select> */}
              <input
                type="time"
                // placeholder="00:00"

                value={
                  zeroPad(state.time.h, 2) + ":" + zeroPad(state.time.m, 2)
                }
                className={
                  state.timeError.length < 1
                    ? "app-input mr-1"
                    : "app-input mr-1 invalid"
                }
                onChange={(e) => {
                  const time = e.target.value.split(":");
                  setState({
                    ...state,
                    time: { ...state.time, h: +time[0], m: +time[1] },
                    timeError: "",
                  });
                }}
              />
            </div>
            <label className="error-message">{state.timeError}</label>
          </Col>
          <Col xs={3}>
            <label className="font-weight-bold form-label">
              Duration in days
              <span className="ml-1" style={{ color: "red" }}>
                *
              </span>
            </label>
            <input
              className={
                state.durationError.length < 1
                  ? "app-input"
                  : "app-input invalid"
              }
              style={{ width: "70%" }}
              type="number"
              value={state.duration}
              onChange={(e) =>
                setState({
                  ...state,
                  duration: +e.target.value,
                  durationError: "",
                })
              }
            />
            <label className="error-message">{state.durationError}</label>
          </Col>
        </Row>
        <Row className="py-2">
          <Col xs={12}>
            <label className="font-weight-bold form-label">Documents</label>
            <div className="d-flex align-items-center">
              <div style={{ marginRight: "100px" }}>
                <button
                  className="app-button"
                  style={{ fontWeight: "lighter" }}
                  onClick={() => {
                    uploadElement.click();
                  }}
                >
                  <MdAdd className="mr-2" style={{ fontSize: "26px" }} />
                  Attach File
                </button>
              </div>
              <div
                {...getRootProps()}
                className="drop-zone d-flex align-items-center"
                style={{
                  border: "2px dashed #a5a5a5",
                  padding: "4px",
                  paddingLeft: "150px",
                  paddingRight: "150px",
                  color: "#a5a5a5",
                  fontSize: "18px",
                }}
              >
                <span className="mr-2" style={{ fontSize: "26px" }}>
                  <IoMdDownload />
                </span>{" "}
                Drop files Here
              </div>
            </div>

            <div>
              {selectedFiles.map((f) => (
                <FileTag
                  file={f}
                  onClose={(f) => {
                    setSelectedFiles((ps) => {
                      return Object.assign(
                        [],
                        ps.filter((f2) => f2.id !== f.id)
                      );
                    });
                  }}
                />
              ))}
            </div>
            <input
              type="file"
              {...getInputProps()}
              ref={(input) => setUploadElement(input)}
              accept=".xls, .pptx, .ppt, .png, .pdf, .jpg, .jpeg, .gif, .docx, .doc, .xlsx, .txt, .md"
              className="d-none"
              onChange={(e) => {
                const file: any = e.target.files ? e.target.files[0] : null;
                const fileType = file?.name
                  .split(".")
                  .pop()
                  .toLowerCase();
                const exList = "xls,pptx,ppt,png,pdf,jpg,jpeg,gif,docx,doc,xlsx,txt,md".split(
                  ","
                );

                let b1 =
                  file && exList.filter((ext) => ext === fileType).length > 0;
                let b2 = file && file?.size <= 105255200;
                let errorMessage = "No file was selected";
                if (b1) errorMessage = "Invalid file format";
                if (b2) errorMessage = " Invalid file size";
                if (b1 && b2) {
                  if (file) {
                    setSelectedFiles((ps) => {
                      ps.push({
                        file: file,
                        id: randomString.generate(),
                        url: "",
                      });
                      return Object.assign([], ps);
                    });
                  }
                } else {
                  UserNotification({
                    isSuccess: false,
                    message: errorMessage,
                  });
                }
              }}
            />
          </Col>
        </Row>
        <Row className="py-2">
          <Col xs={4}>
            <label className="font-weight-bold form-label">
              Post to
              <span className="ml-1" style={{ color: "red" }}>
                *
              </span>
            </label>
            <Select
              styles={
                state.countriesError.length < 1
                  ? customStylesValid
                  : customStylesInValid
              }
              isMulti
              placeholder="Select Countries"
              options={[
                { value: Country.SRI_LANKA, name: "Sri Lanka" },
                { value: Country.JAMAICA, name: "Jamaica" },
                { value: Country.MALAYSIA, name: "Malaysia" },
                { value: Country.BOSNIA, name: "Bosnia and Herzegovina" },
                { value: Country.INDIA, name: "India" },
                { value: Country.INDONESIA, name: "Indonesia" },
              ].map((country) => ({
                value: country.value,
                label: country.name,
              }))}
              onChange={(e: any) => {
                const list = (e as { value: string; label: string }[]) || [];
                console.log(list);
                setState((ps) => {
                  const cList = list.map((c) => c.value as Country) || [];
                  return { ...ps, countries: cList, countriesError: "" };
                });
              }}
            />
            <label className="error-message">{state.countriesError}</label>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CreateAnnouncement;
